import { ChangePlanErrorType, SwitchPlanType } from '../../../../enums';
import { AppStore, STEP_ACTIONS } from '../../../../stores/App.store';
import { ChangePlanDetailsResult } from '../../ChangePlanTypes';
import { SentryLogger } from '../../../../services/SentryLogger';
import Api from '../../../../services/Api/Api';
import * as React from 'react';
import { getApiParams, getBiParams } from '../../changePlanUtils';
import { PaymentCycle_BIMap } from 'services/BiLogger/BiLoggerTypes';

export const useChangePlanModal = (appStore: AppStore) => {
  const [
    changePlanDetails,
    setChangePlanDetails,
  ] = React.useState<ChangePlanDetailsResult | null>(null);
  const [error, setError] = React.useState<ChangePlanErrorType>(
    ChangePlanErrorType.NONE,
  );
  const [isSuccessModal, setSuccessModal] = React.useState<boolean>(false);
  const {
    currentProduct,
    appContext,
    closeChangePlanModal,
    changeStep,
    currentCycle,
    isDowngradePlanOfDeclineChargeService,
  } = appStore;
  const { isDynamicOffering, currentPlanDetails, biLogger } = appContext;
  const { userPackageGuid } = currentPlanDetails!;
  React.useEffect(() => {
    // https://stackoverflow.com/questions/53949393/cant-perform-a-react-state-update-on-an-unmounted-component
    let isMounted = true;
    const fetchData = async () => {
      try {
        const changePlanDetailsResult: ChangePlanDetailsResult = isDynamicOffering
          ? await Api.fetchDynamicChangePlanDetails({
              targetProductId: currentProduct?.guid ?? '',
              subscriptionId: userPackageGuid,
            })
          : await Api.fetchChangePlanDetails({
              targetProductId: currentProduct.id,
              userPackageGuid,
            });

        if (isMounted) {
          setChangePlanDetails(changePlanDetailsResult);
          const biEventParams = getBiParams(changePlanDetailsResult, appStore);
          biLogger.logChangePlanPopupOpened(biEventParams);
        }
      } catch (err) {
        SentryLogger.logError(err);
        if (isMounted) {
          setError(ChangePlanErrorType.DATA_RETRIEVAL_FAILED);
        }
      }
    };

    void fetchData();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleChangePlan = async () => {
    const { changePlanType } = changePlanDetails!;
    const {
      removeUnloadListener,
      baseUrl,
      appContext: { siteGuid },
    } = appStore;
    const { switchParams, api } = getApiParams(isDynamicOffering, appStore);

    biLogger.logChangePlanPopupClickChange(
      getBiParams(changePlanDetails!, appStore),
    );
    setChangePlanDetails(null);
    try {
      if (isDowngradePlanOfDeclineChargeService) {
        closeChangePlanModal();
        changeStep(
          STEP_ACTIONS.SPECIFIC,
          (from, to) => {
            appContext.biLogger.logStepBackBiEvent({
              fromStep: `change_${to}_from_${from}_bar`,
              productId: currentProduct.id,
              productGuid: currentProduct.guid,
              cycle: PaymentCycle_BIMap[currentCycle],
            });
          },
          2,
        );
        return;
      }
      switch (changePlanType) {
        case SwitchPlanType.Extend:
          await api.switchAndExtend(switchParams);
          setSuccessModal(true);
          break;
        case SwitchPlanType.Refund:
          await api.switchAndRefund(switchParams);
          setSuccessModal(true);
          break;
        case SwitchPlanType.GracePeriod:
        default:
          await api.switchInGracePeriod(switchParams);
          removeUnloadListener && removeUnloadListener();
          window.location.assign(
            `${baseUrl}dashboard/${siteGuid}/settings/subscriptions/?referralInfo=AdvancedPlans_changePlan`,
          );
      }
    } catch (err: any) {
      SentryLogger.logError(err);
      const { data } = err.data ? err : err.response;
      const errorType =
        data?.errorDescription?.indexOf('Invalid EU Vat Number') > -1
          ? ChangePlanErrorType.INVALID_EU_VAT
          : ChangePlanErrorType.CHANGE_FAILED;
      setError(errorType);
    }
  };

  const isErrorModal = error !== ChangePlanErrorType.NONE;

  const onCloseChangePlanModal = () => {
    biLogger.logChangePlanPopupClosed(
      getBiParams(changePlanDetails!, appStore),
    );
    closeChangePlanModal();
  };

  const confirmTextKey =
    changePlanDetails &&
    changePlanDetails.changePlanType === SwitchPlanType.GracePeriod
      ? 'change_plan.change_plan_modal.continue'
      : 'change_plan.change_plan_modal.confirm_change';

  const changePlanModalProps = {
    confirmTextKey,
    handleChangePlan,
    onCloseChangePlanModal,
  };

  return {
    isSuccessModal,
    isErrorModal,
    error,
    changePlanDetails,
    changePlanModalProps,
    isDeclineCharge: isDowngradePlanOfDeclineChargeService,
  };
};
